import BigNumber from 'bignumber.js';
import { Rfq } from '#/unified-rfqs/entities/rfq';
import { AuctionType } from '#/unified-rfqs/entities/shared';
import { FormOption } from '#/unified-rfqs/ui/rfq-builder/types';
import { Instrument } from '#/unified-rfqs/entities/drfq/instruments';
import { calcDrfqNotional } from '#/unified-rfqs/entities/drfq/domain';

/**
 * The filter the user submitted, flattened to what actually narrows the RFQ
 * list. Venue/asset/instrument selections reach us as the `productCodes` they
 * resolve to.
 */
export interface RfqFilters {
  readonly auctionTypes: readonly FormOption<AuctionType>[];
  readonly productCodes: readonly string[];
  readonly strategyCodes: readonly string[];
  readonly counterparties?: readonly string[];
  readonly notional: { readonly min: string; readonly max: string };
  /**
   * Set when the product selection resolves to no product at all, which empty
   * `productCodes` cannot express on its own — see
   * `hasConflictingProductSelection`.
   */
  readonly hasConflictingProductSelection?: boolean;
}

type AuctionTypeToRfqTypeMap = { [S in AuctionType]: Rfq['type'] };

const AuctionToRfqMap: AuctionTypeToRfqTypeMap = {
  OB: 'grfq',
  RFQ: 'drfq',
};

/**
 * Whether the RFQ survives the filter. The markets table filters its list with
 * it, and the notification service uses it to stay quiet about what the table
 * hides. Liveness is a separate question — the table pairs this with
 * `isActiveRfq`.
 *
 * @param drfqFutureInstruments Mark prices the notional of a DRFQ is derived
 *   from. Without them the notional bounds cannot be evaluated and are skipped.
 */
export function matchesRfqFilters(
  rfq: Rfq,
  filters: RfqFilters,
  drfqFutureInstruments: readonly Instrument[],
): boolean {
  if (filters.hasConflictingProductSelection === true) return false;

  return (
    hasProduct(rfq, filters.productCodes) &&
    hasStrategy(rfq, filters.strategyCodes) &&
    hasAuctionType(rfq, filters.auctionTypes) &&
    hasCounterparty(rfq, filters.counterparties) &&
    isWithinNotionalRange(rfq, filters.notional, drfqFutureInstruments)
  );
}

function hasStrategy(rfq: Rfq, strategies: readonly string[]): boolean {
  if (strategies.length > 0) {
    return strategies.includes(rfq.strategy_code);
  }
  return true;
}

function hasProduct(rfq: Rfq, productCodes: readonly string[]): boolean {
  if (productCodes.length > 0) {
    return rfq.product_codes.some((code) => productCodes.includes(code));
  }

  return true;
}

function hasCounterparty(
  rfq: Rfq,
  counterparties?: readonly string[],
): boolean {
  if (!counterparties?.length) return true;
  if (rfq.type !== 'drfq') return true;
  if (rfq.is_taker_anonymous) return false;
  if (rfq.taker_desk_name == null) return false;

  return counterparties.includes(rfq.taker_desk_name);
}

function hasAuctionType(
  rfq: Rfq,
  auctionTypes: readonly FormOption<AuctionType>[],
): boolean {
  if (auctionTypes.length === 0) return true;
  return auctionTypes.some((type) => AuctionToRfqMap[type.value] === rfq.type);
}

function isWithinNotionalRange(
  rfq: Rfq,
  notional: { min: string; max: string },
  drfqFutureInstruments: readonly Instrument[],
): boolean {
  if (rfq.type !== 'drfq') return true;
  if (drfqFutureInstruments.length === 0) return true;

  const minNotional = BigNumber(notional.min);
  const maxNotional = BigNumber(notional.max);
  if (minNotional.isNaN() && maxNotional.isNaN()) return true;

  const rfqNotional = calcDrfqNotional(rfq, drfqFutureInstruments);
  if (rfqNotional.isNaN() || rfqNotional.isZero()) return true;

  return (
    (minNotional.isNaN() || rfqNotional.gte(minNotional)) &&
    (maxNotional.isNaN() || rfqNotional.lte(maxNotional))
  );
}
